import { Link, NavLink, useLocation } from "react-router-dom";
import { Heart, Flame, MessageCircle, Settings, CreditCard } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "../lib/utils";

const NAV_ITEMS = [
  { to: "/deck", label: "Entdecken", icon: Flame },
  { to: "/matches", label: "Matches", icon: MessageCircle },
  { to: "/abo", label: "Abo", icon: CreditCard },
  { to: "/einstellungen", label: "Profil", icon: Settings },
];

interface AppShellProps {
  children: ReactNode;
  /** Wenn true, wird die Navi ausgeblendet (fuer Vollbild-Screens wie Chat). */
  hideNav?: boolean;
}

export function AppShell({ children, hideNav }: AppShellProps) {
  const location = useLocation();

  return (
    <div className="min-h-screen bg-surface">
      <header className="sticky top-0 z-30 border-b border-border bg-white/80 backdrop-blur-xl">
        <div className="container flex items-center justify-between py-3">
          <Link to="/deck" className="flex items-center gap-2">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-xl bg-brand-gradient shadow-glow">
              <Heart className="h-4 w-4 text-white" fill="currentColor" />
            </span>
            <span className="text-lg font-bold tracking-tight">Parble</span>
          </Link>
          {!hideNav && (
            <nav className="hidden md:flex items-center gap-1">
              {NAV_ITEMS.map(({ to, label, icon: Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  className={({ isActive }) =>
                    cn(
                      "inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition",
                      isActive
                        ? "bg-brand-gradient text-white shadow-glow"
                        : "text-ink-500 hover:bg-surface hover:text-ink-900",
                    )
                  }
                >
                  <Icon className="h-4 w-4" />
                  {label}
                </NavLink>
              ))}
            </nav>
          )}
        </div>
      </header>

      <main className="pb-24 md:pb-10">{children}</main>

      {!hideNav && (
        <nav className="fixed bottom-0 left-0 right-0 z-30 border-t border-border bg-white/90 backdrop-blur-xl md:hidden">
          <div className="grid grid-cols-4 px-2 py-2">
            {NAV_ITEMS.map(({ to, label, icon: Icon }) => {
              const isActive = location.pathname.startsWith(to);
              return (
                <Link
                  key={to}
                  to={to}
                  className={cn(
                    "flex flex-col items-center gap-1 rounded-2xl py-2 text-[11px] font-semibold",
                    isActive ? "text-brand-600" : "text-ink-500",
                  )}
                >
                  <Icon className={cn("h-5 w-5", isActive && "text-brand-500")} />
                  {label}
                </Link>
              );
            })}
          </div>
        </nav>
      )}
    </div>
  );
}
